/* eslint-disable no-underscore-dangle, class-methods-use-this, no-useless-constructor, @typescript-eslint/no-unused-vars, prefer-promise-reject-errors */
import { error as errorNotifications } from 'react-notification-system-redux';

import {
  cancelBracketOrderPosition,
  closePosition,
  editBracketOrder,
  placeBracketOrderPosition,
  positionBracketOrderMessage,
} from 'actions/trade';
import { SOURCE_FOR_MIXPANEL_EVENTS } from 'constants/enums';
import { getDeviceSourcePayload } from 'helpers/utils';
import StoreProxy from 'storeProxy';
import { OpenPosition } from 'types/IOpenPosition';
import { OpenStopOrders } from 'types/IOpenStopOrders';
import { Product } from 'types/IProducts';

import {
  AccountId,
  AccountManagerInfo,
  AccountMetainfo,
  ActionMetaInfo,
  Brackets,
  ConnectionStatus,
  DefaultContextMenuActionsParams,
  Execution,
  IBrokerConnectionAdapterHost,
  InstrumentInfo,
  INumberFormatter,
  IsTradableResult,
  Order,
  PlaceOrderResult,
  Position,
  PreOrder,
  TradeContext,
} from '../../charting_library_trading_platform/charting_library';
import { AbstractBroker, UpdatePositionOrderType } from './AbstractBroker';
import {
  trackClosePositionSuccess,
  trackPlaceBracketOrderInteraction,
} from './brokerUtils';
import { createOrderFromOpenPosition } from './openPositionLine';

export class Broker extends AbstractBroker {
  private _activeSymbol: string | undefined = undefined;

  constructor(host: IBrokerConnectionAdapterHost) {
    super(host);
  }

  setActiveSymbol(symbol: string) {
    if (this._activeSymbol === symbol) {
      return;
    }
    this._activeSymbol = symbol;
    this._host.ordersFullUpdate();
    this._host.positionsFullUpdate();
  }

  private _getBrokerOrderFromDeltaOrder(order: OpenStopOrders): Order {
    const orderObj = {
      id: String(order?.id),
      symbol: order?.product_symbol ?? '',
      type: order?.stop_order_type === 'take_profit_order' ? 1 : 3,
      qty: Number(order?.size),
      side: order?.side === 'sell' ? -1 : 1,
      parentId: String(order?.product_id), // product id is the position id
      parentType: 2,
      status: 6,
    };
    const priceKey =
      order?.stop_order_type === 'take_profit_order' ? 'limitPrice' : 'stopPrice';
    orderObj[priceKey] = Number(order?.stop_price);
    return orderObj;
  }

  private _getBracketOrders(): Order[] {
    const allStopOrders =
      StoreProxy?.getState()?.holdings?.openStopOrders?.futures?.data ?? [];
    const stopOrders = allStopOrders
      .filter(
        (stopOrder: OpenStopOrders) =>
          stopOrder?.bracket_order &&
          (stopOrder.stop_order_type === 'stop_loss_order' ||
            stopOrder.stop_order_type === 'take_profit_order')
      )
      .map((order: OpenStopOrders) => this._getBrokerOrderFromDeltaOrder(order));
    return stopOrders;
  }

  orders(): Promise<Order[]> {
    return new Promise(resolve => {
      const bracketOrders = this._getBracketOrders();
      let ordersToBeResolved = bracketOrders;
      if (this._activeSymbol?.startsWith('MARK:')) {
        ordersToBeResolved = this._getPositionsOrdersWithMarkSymbols(
          bracketOrders
        ) as Order[];
      }
      resolve(ordersToBeResolved);
    });
  }

  private _getBrokerPositionFromDeltaPosition(position: OpenPosition): Position {
    return {
      id: String(position?.product_id) ?? '',
      symbol: position?.product_symbol ?? '',
      qty: Number(position?.size),
      side: Number(position?.size) > 0 ? 1 : -1,
      avgPrice: Number(position?.entry_price),
    };
  }

  positions(): Promise<Position[]> {
    return new Promise(resolve => {
      const openPositions = StoreProxy.getState()?.holdings?.openPositions?.futures
        ?.data as OpenPosition[];

      const openBrokerPositions = openPositions.map((position: OpenPosition) => {
        return this._getBrokerPositionFromDeltaPosition(position);
      });
      let positionToBeResolved = openBrokerPositions;
      if (this._activeSymbol?.startsWith('MARK:')) {
        positionToBeResolved = this._getPositionsOrdersWithMarkSymbols(
          openBrokerPositions
        ) as Position[];
      }
      resolve(positionToBeResolved);
    });
  }

  private _getPositionsOrdersWithMarkSymbols(
    openBrokerPositionsOrders: Position | Order | Position[] | Order[]
  ) {
    if (!Array.isArray(openBrokerPositionsOrders)) {
      return {
        ...openBrokerPositionsOrders,
        symbol: `MARK:${openBrokerPositionsOrders.symbol}`,
      };
    }

    return openBrokerPositionsOrders.map((entity: Position | Order) => {
      return {
        ...entity,
        symbol: `MARK:${entity.symbol}`,
      };
    });
  }

  async modifyOrder(order: Order): Promise<void> {
    return new Promise((resolve, reject) => {
      const stopOrder: OpenStopOrders =
        StoreProxy?.getState()?.holdings?.openStopOrders?.all?.data?.find(
          (stopOrderItem: OpenStopOrders) => String(stopOrderItem.id) === order.id
        );
      if (!stopOrder) {
        reject(new Error('Order not found'));
        return;
      }
      if (stopOrder?.stop_order_type === 'stop_loss_order') {
        const openPosition =
          StoreProxy?.getState()?.holdings?.openPositions?.all?.data?.find(
            (positionItem: OpenPosition) =>
              positionItem.product_id === stopOrder.product_id
          );
        const liquidationPrice = Number(openPosition?.liquidation_price);
        if (liquidationPrice && order?.stopPrice) {
          if (openPosition.size > 0 && order.stopPrice < liquidationPrice) {
            StoreProxy.dispatch(
              errorNotifications({
                title: 'Bracket Order Fail',
                message: 'Trigger Price set cannot be less than liquidation price.',
                position: 'bl',
              })
            );
            reject(new Error('Stop price should be less than liquidation price'));
            return;
          }
          if (openPosition.size < 0 && order.stopPrice > liquidationPrice) {
            StoreProxy.dispatch(
              errorNotifications({
                title: 'Bracket Order Fail',
                message: 'Trigger Price set cannot be more than liquidation price.',
                position: 'bl',
              })
            );
            reject(new Error('Stop price should be greater than liquidation price'));
            return;
          }
        }
      }

      const payload = {
        id: stopOrder.id,
        product_id: stopOrder.product_id,
        stop_price: order.limitPrice ?? order.stopPrice,
      };
      StoreProxy.dispatch(
        editBracketOrder(payload, {}, (result, error) => {
          if (error) {
            reject(error);
            return;
          }
          trackPlaceBracketOrderInteraction(
            stopOrder,
            'UPDATE',
            stopOrder?.stop_order_type === 'take_profit_order'
              ? 'take_profit'
              : 'stop_loss'
          );
          resolve();
        })
      );
    });
  }

  cancelOrder(orderId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const bracketOrders = this._getBracketOrders();
      const order = bracketOrders.find(orderItem => orderItem.id === orderId);
      if (!order) {
        reject(new Error('Order not found'));
        return;
      }
      StoreProxy.dispatch(
        cancelBracketOrderPosition(
          { id: orderId, product_symbol: order.symbol },
          {
            source: SOURCE_FOR_MIXPANEL_EVENTS.BROKER_CHART,
          },
          (result, error) => {
            if (error) {
              reject(error);
              return;
            }
            const openPosition =
              StoreProxy.getState()?.holdings?.openPositions?.all?.data?.find(
                (positionItem: OpenPosition) =>
                  String(positionItem?.product_id) === order.parentId
              );
            trackPlaceBracketOrderInteraction(
              openPosition,
              'CANCEL',
              order.type === 1 ? 'take_profit' : 'stop_loss'
            );
            resolve();
          }
        )
      );
    });
  }

  closePosition(positionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const position: OpenPosition =
        StoreProxy.getState()?.holdings?.openPositions?.all?.data?.find(
          (positionItem: OpenPosition) => String(positionItem?.product_id) === positionId
        );
      if (!position) {
        reject(new Error('Position not found'));
        return;
      }
      const order = createOrderFromOpenPosition(position);
      StoreProxy.dispatch(
        closePosition({ ...order, ...getDeviceSourcePayload() }, {}, (result, error) => {
          if (error) {
            reject(error);
            return;
          }
          trackClosePositionSuccess(position);
          resolve();
        })
      );
    });
  }

  // to be called from app, outside of broker; mostly synced with socket positions actions
  handleUpdatePositions(position: OpenPosition, type: UpdatePositionOrderType): void {
    switch (type) {
      case 'SNAPSHOT':
      case 'CREATE':
      case 'UPDATE': {
        let positionToBeUpdated = this._getBrokerPositionFromDeltaPosition(position);
        if (this._activeSymbol?.startsWith('MARK:')) {
          positionToBeUpdated = this._getPositionsOrdersWithMarkSymbols(
            positionToBeUpdated
          ) as Position;
        }
        this._host.positionUpdate(positionToBeUpdated);
        break;
      }
      case 'DELETE': {
        let positionToBeDeleted = this._getBrokerPositionFromDeltaPosition(position);
        if (this._activeSymbol?.startsWith('MARK:')) {
          positionToBeDeleted = this._getPositionsOrdersWithMarkSymbols(
            positionToBeDeleted
          ) as Position;
        }
        this._host.positionUpdate({
          ...positionToBeDeleted,
          qty: 0,
        });
        break;
      }
      default:
        break;
    }
  }

  // to be called from app, outside of broker; mostly synced with socket orders actions
  handleUpdateOrders(order: OpenStopOrders, type: UpdatePositionOrderType): void {
    try {
      switch (type) {
        case 'SNAPSHOT':
        case 'CREATE':
        case 'UPDATE': {
          let orderToBeUpdated = this._getBrokerOrderFromDeltaOrder(order);
          if (this._activeSymbol?.startsWith('MARK:')) {
            orderToBeUpdated = this._getPositionsOrdersWithMarkSymbols(
              orderToBeUpdated
            ) as Order;
          }
          this._host.orderUpdate(orderToBeUpdated);
          break;
        }
        case 'DELETE': {
          let orderToBeDeleted = this._getBrokerOrderFromDeltaOrder(order);
          if (this._activeSymbol?.startsWith('MARK:')) {
            orderToBeDeleted = this._getPositionsOrdersWithMarkSymbols(
              orderToBeDeleted
            ) as Order;
          }
          this._host.orderUpdate({
            ...orderToBeDeleted,
            status: 1,
          });
          break;
        }
        default:
          break;
      }
    } catch (error) {
      console.error('Error in Broker handleUpdateOrders', error);
    }
  }

  // to be called from app, outside of broker; to update PnL from mark price changes
  handlePLUpdate(positionId: string, markPrice: number): void {
    const position: OpenPosition =
      StoreProxy?.getState()?.holdings?.openPositions?.all?.data?.find(
        (positionItem: OpenPosition) => String(positionItem?.product_id) === positionId
      );
    if (!position || !position.entry_price || !position.size) {
      return;
    }
    const unrealisedPL =
      (markPrice - Number(position.entry_price)) *
      position.size *
      Number(position.product.contract_value);
    this._host.plUpdate(positionId, unrealisedPL);
  }

  chartContextMenuActions(
    context: TradeContext,
    options?: DefaultContextMenuActionsParams | undefined
  ): Promise<ActionMetaInfo[]> {
    return Promise.resolve([]); // to hide trade buttons in context menus
  }

  async isTradable(symbol: string): Promise<boolean | IsTradableResult> {
    return Promise.resolve(true);
  }

  connectionStatus(): ConnectionStatus {
    return 1;
  }

  executions(symbol: string): Promise<Execution[]> {
    return Promise.resolve([]);
  }

  async symbolInfo(symbol: string): Promise<InstrumentInfo> {
    let symbolToBeUsed = symbol;
    if (symbol.startsWith('MARK:')) {
      symbolToBeUsed = symbol.slice(5);
    }
    const products = StoreProxy.getState()?.trade?.products as { [key: string]: Product };
    const product = Object.values(products).find(
      (productItem: Product) => productItem.symbol === symbolToBeUsed
    );
    const contractValue = Number(product?.contract_value);
    const mintick = Number(product?.tick_size);
    if (Number.isNaN(contractValue) || Number.isNaN(mintick)) {
      return Promise.reject();
    }
    return {
      qty: {
        min: 1,
        max: 1e12,
        step: 1,
      },
      pipValue: contractValue * mintick,
      pipSize: mintick,
      minTick: mintick,
      description: symbol,
    };
  }

  accountManagerInfo(): AccountManagerInfo {
    return {
      accountTitle: '',
      summary: [],
      orderColumns: [],
      positionColumns: [],
      pages: [],
    };
  }

  async accountsMetainfo(): Promise<AccountMetainfo[]> {
    return [
      {
        id: '1' as AccountId,
        name: '',
        currency: 'USD',
      },
    ];
  }

  currentAccount(): AccountId {
    return '1' as AccountId;
  }

  quantityFormatter(symbol: string): Promise<INumberFormatter> {
    return this._host.quantityFormatter();
  }

  formatter(symbol: string): Promise<INumberFormatter> {
    if (symbol.startsWith('MARK:')) {
      return this._host.defaultFormatter(symbol.slice(5), true);
    }
    return this._host.defaultFormatter(symbol, true);
  }

  spreadFormatter(symbol: string): Promise<INumberFormatter> {
    if (symbol.startsWith('MARK:')) {
      return this._host.defaultFormatter(symbol.slice(5), true);
    }
    return this._host.defaultFormatter(symbol, true);
  }

  showNotification(title: string, message: string): void {
    this._host.showNotification(title, message);
  }

  async editPositionBrackets(positionId: string, brackets: Brackets): Promise<void> {
    return new Promise((resolve, reject) => {
      const openPosition: OpenPosition =
        StoreProxy?.getState()?.holdings?.openPositions?.all?.data?.find(
          (position: OpenPosition) => String(position.product_id) === positionId
        );
      if (!openPosition || !openPosition?.size) {
        reject('Position not found');
        return;
      }
      if (brackets.takeProfit && !openPosition?.bracket_orders?.take_profit_order?.id) {
        const tpPayload = this._getBracketTPPayload(positionId, brackets.takeProfit);
        StoreProxy.dispatch(
          placeBracketOrderPosition(tpPayload, (result, error) => {
            if (error) {
              reject(error);
              return;
            }
            trackPlaceBracketOrderInteraction(openPosition, 'CREATE', 'take_profit');
            StoreProxy.dispatch(
              positionBracketOrderMessage({
                title: 'Bracket Order',
                position: 'bl',
                message: `${
                  openPosition?.product?.symbol ?? ''
                }: Take Profit order created successfully`,
              })
            );
            resolve();
          })
        );
      }
      if (brackets.stopLoss && !openPosition?.bracket_orders?.stop_loss_order?.id) {
        // liquidation price validation; for long -> stop loss should be greater than liquidation price and for short -> stop loss should be less than liquidation price
        if (
          (openPosition.size > 0 &&
            brackets.stopLoss < Number(openPosition?.liquidation_price)) ||
          (openPosition.size < 0 &&
            brackets.stopLoss > Number(openPosition?.liquidation_price))
        ) {
          reject();
          const msg =
            openPosition.size > 0
              ? 'Trigger Price set cannot be less than liquidation price.'
              : 'Trigger Price set cannot be more than liquidation price.';
          StoreProxy.dispatch(
            errorNotifications({
              title: 'Bracket Order Fail',
              message: msg,
              position: 'bl',
            })
          );
          return;
        }
        const slPayload = this._getBracketSLPayload(positionId, brackets.stopLoss);
        StoreProxy.dispatch(
          placeBracketOrderPosition(slPayload, (result, error) => {
            if (error) {
              reject(error);
              return;
            }
            trackPlaceBracketOrderInteraction(openPosition, 'CREATE', 'stop_loss');
            StoreProxy.dispatch(
              positionBracketOrderMessage({
                title: 'Bracket Order',
                position: 'bl',
                message: `${
                  openPosition?.product?.symbol ?? ''
                }: Stop Loss order created successfully`,
              })
            );
            resolve();
          })
        );
      }
    });
  }

  private _getBracketTPPayload(positionId: string, takeProfit: number) {
    return {
      take_profit_order: {
        order_type: 'market_order',
        stop_price: takeProfit,
      },
      bracket_stop_trigger_method: 'mark_price',
      product_id: Number(positionId),
      ...getDeviceSourcePayload(),
    };
  }

  private _getBracketSLPayload(positionId: string, stopLoss: number) {
    return {
      stop_loss_order: {
        order_type: 'market_order',
        stop_price: stopLoss,
      },
      bracket_stop_trigger_method: 'mark_price',
      product_id: Number(positionId),
      ...getDeviceSourcePayload(),
    };
  }

  async placeOrder(order: PreOrder): Promise<PlaceOrderResult> {
    return {};
  }
}
